import { User } from "../types";

const DB_NAME = "ZetaAdminDB";
const DB_VERSION = 1;
const STORE_NAME = "auth";

class StorageService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error("Failed to open IndexedDB"));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "key" });
        }
      };
    });
  }

  private async getStore(
    mode: IDBTransactionMode = "readonly"
  ): Promise<IDBObjectStore> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    // Check if object store exists, if not, recreate the database
    if (!this.db.objectStoreNames.contains(STORE_NAME)) {
      console.warn("Object store not found, recreating database...");
      this.db.close();
      // Delete the existing database and recreate it
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      await new Promise<void>((resolve, reject) => {
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () =>
          reject(new Error("Failed to delete database"));
      });
      // Reinitialize with fresh database
      await this.init();
      if (!this.db) {
        throw new Error("Failed to reinitialize database");
      }
    }

    const transaction = this.db.transaction([STORE_NAME], mode);
    return transaction.objectStore(STORE_NAME);
  }

  async setItem(key: string, value: any): Promise<void> {
    const store = await this.getStore("readwrite");
    return new Promise((resolve, reject) => {
      const request = store.put({ key, value, timestamp: Date.now() });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to store item"));
    });
  }

  async getItem(key: string): Promise<any> {
    const store = await this.getStore();
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      request.onerror = () => reject(new Error("Failed to retrieve item"));
    });
  }

  async removeItem(key: string): Promise<void> {
    const store = await this.getStore("readwrite");
    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to remove item"));
    });
  }

  async clear(): Promise<void> {
    const store = await this.getStore("readwrite");
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to clear store"));
    });
  }

  // Auth-specific methods
  async saveAuth(token: string, user: User): Promise<void> {
    await this.setItem("authToken", token);
    await this.setItem("user", user);
  }

  async getAuthToken(): Promise<string | null> {
    return await this.getItem("authToken");
  }

  async getUser(): Promise<User | null> {
    return await this.getItem("user");
  }

  async clearAuth(): Promise<void> {
    await this.removeItem("authToken");
    await this.removeItem("user");
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.getAuthToken();
    return !!token;
  }
}

export const storageService = new StorageService();
